/**
 * AN ID THAT ARRIVES INSIDE A FILE IS TEXT, NOT A NAME THE APP CHOSE (2026-10-03).
 *
 * A character's `id`, a combatant's, a token's key all become document ids
 * (`sessions/S/characters/{id}`) once the file is imported. The app mints its
 * own ids from `[A-Za-z0-9_-]`, but a hand-edited or crafted file can carry
 * anything: a "/" splits the path (PocketBase 400 "Missing collection
 * context", Firestore a nested path), "." and ".." and `__x__` are refused by
 * Firestore, and an overlong one passes PocketBase's 200-character record id
 * once the session prefix is on it. The same trap a "/" in a display name was
 * (gotchas, 2026-10-02) - so every import path checks its ids at the door.
 *
 * A GOOD ID IS KEPT, so an export re-imports onto the same documents. A bad
 * one gets a STAND-IN derived from it (FNV-1a), not a random one: the same
 * file imported twice lands on the same ids, as a good file does, and no
 * Math.random is spent (gotcha 5). Whatever else in the file pointed at the
 * bad id - a token keyed by it, the active tab, a tracker entry - is pointed
 * at the stand-in by the same pass. An EMPTY or missing id is not touched
 * here: nothing can point at it, and each reader already has its fallback.
 */
import { fnv1aHex } from '../utils/fnv1a';

/** What both backends store verbatim as a path segment - PocketBase's id pattern, and safe in Firestore. */
const STORABLE = /^[A-Za-z0-9_-]+$/;
/** Firestore reserves ids that begin and end with two underscores. */
const RESERVED = /^__.*__$/;
/**
 * Room under PocketBase's 200-character record id for the session prefix
 * (`sessions_<id>_`) and for `castDocId`'s `<sceneId>--<combatantId>`.
 */
const MAX_IMPORTED_ID = 128;

/** True when `id` can name a document on both backends as it is. */
export function isSafeDocId(id: unknown): id is string {
  return typeof id === 'string' && id.length <= MAX_IMPORTED_ID && STORABLE.test(id) && !RESERVED.test(id);
}

/** The id to use for one a file carried: itself when safe, else a stable stand-in derived from it. */
export function standInId(id: string): string {
  return isSafeDocId(id) ? id : `imp-${fnv1aHex(id)}`;
}

/**
 * The parsed file with every unsafe id among `found` replaced by its
 * stand-in, wherever it appears - as a string value or as an object key - so
 * what pointed at it still does. The very same object when there is nothing
 * to replace; never edits the input. A text field that happens to EQUAL a bad
 * id is replaced too: in a file, an exact match is taken to be a reference.
 */
export function remapUnsafeIds<T>(data: T, found: Iterable<unknown>): T {
  const map = new Map<string, string>();
  for (const id of found) if (typeof id === 'string' && id && !isSafeDocId(id)) map.set(id, standInId(id));
  return map.size ? (rewrite(data, map) as T) : data;
}

function rewrite(value: unknown, map: ReadonlyMap<string, string>): unknown {
  if (typeof value === 'string') return map.get(value) ?? value;
  if (Array.isArray(value)) return value.map((v) => rewrite(v, map));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [map.get(k) ?? k, rewrite(v, map)]));
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** A character file's entries: a bare array, a `{ characters }` envelope, or one object. */
function characterEntries(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (isRecord(data) && Array.isArray(data.characters)) return data.characters;
  return [data];
}

/** A parsed character file with every character's id fit to name a document. */
export function withImportableCharacterIds<T>(data: T): T {
  return remapUnsafeIds(
    data,
    characterEntries(data).map((c) => (isRecord(c) ? c.id : undefined)),
  );
}
